/**
 * run.ts
 * フロー実行エンジン
 * Startノードから開始し、接続されたノードを順次/並列に実行する
 */
import {
  type AppEdge,
  type AppNode,
  type AppNodeOf,
  type ExecContext,
  type Logger,
  type NodeRunMeta,
  type NodeRunResult,
} from './types';
import { isNodeKind, nodeRegistry } from './nodeRegistry';
import { SpeechPrefetch } from '@/speech/SpeechPrefetch';
import { collectSpeechPlan } from './speechPlan';
import { OFFLINE_MODE } from '@/config/offline';
import { getNodeDisplayLabel, type FlowDisplayLanguage } from './displayLanguage';

export const getOfflineQrSpeechMessage = (language: FlowDisplayLanguage): string => {
  const qrScan = getNodeDisplayLabel('qrScan', language);
  const keywordSwitch = getNodeDisplayLabel('keywordSwitch', language);
  const textEdit = getNodeDisplayLabel('textEdit', language);
  const sayText = getNodeDisplayLabel('sayText', language);
  return `オフラインでは ${qrScan} で読み取った文字を ${sayText} で話せません（事前作成した10個の文章のみ再生できます）。`
    + `${qrScan} → ${keywordSwitch} → ${textEdit} → ${sayText} のように定型文を選んでつないでください。`;
};

export const OFFLINE_QR_SPEECH_MESSAGE = getOfflineQrSpeechMessage('en');

// Offline Say Text plays only bundled recordings, so free text read from a QR code can never be spoken.
export const isOfflineUnsupportedConnection = (sourceType: string | undefined, targetType: string | undefined) =>
  sourceType === 'qrScan' && targetType === 'sayText';

export function findOfflineFlowIssue(
  nodes: AppNode[],
  edges: AppEdge[] = [],
  displayLanguage: FlowDisplayLanguage = 'en',
): string | null {
  const types = new Map(nodes.map((node) => [node.id, node.type]));
  for (const node of nodes) {
    if (node.type === 'listen') return 'Listen カードはオフラインでは使用できません。削除してから実行してください。';
    if (node.type === 'function') {
      const issue = findOfflineFlowIssue(node.data.internalNodes, node.data.internalEdges, displayLanguage);
      if (issue) return issue;
    }
  }
  if (edges.some((edge) => isOfflineUnsupportedConnection(types.get(edge.source), types.get(edge.target)))) {
    return getOfflineQrSpeechMessage(displayLanguage);
  }
  return null;
}

/** コンテキストのシャローコピー（並列分岐時にデータを分離するため） */
const cloneCtx = (ctx: ExecContext): ExecContext => ({
  ...ctx,
  counterStates: ctx.counterStates
    ? Object.fromEntries(Object.entries(ctx.counterStates).map(([nodeId, state]) => [nodeId, { ...state }]))
    : undefined,
});

/** エッジハイライト用コールバック型 */
export type EdgeHighlighter = (edgeId: string, active: boolean) => void;
export type NodeHighlighter = (nodeId: string, active: boolean) => void;

const MAX_TOTAL_STEPS = 5000;
const MIN_EDGE_HIGHLIGHT_MS = 500;

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (ms <= 0 || signal?.aborted) {
      resolve();
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    signal?.addEventListener('abort', onAbort, { once: true });
  });

const pickBranchEdges = (outs: AppEdge[], sourceHandle: string): AppEdge[] => {
  const matched = outs.filter((edge) => edge.sourceHandle === sourceHandle);
  if (matched.length > 0) {
    return matched;
  }
  // 旧フロー（handle未指定）互換
  return outs.filter((edge) => edge.sourceHandle == null);
};

/**
 * ノード実行関数
 * nodeRegistry から対応する runner を取得して実行する
 * isNodeKind チェック後に呼び出すこと
 */
function executeNode(node: AppNode, ctx: ExecContext, log: Logger, meta?: NodeRunMeta): Promise<NodeRunResult> | NodeRunResult {
  if (!isNodeKind(node.type)) {
    return;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return nodeRegistry[node.type].run(node as any, ctx, log, meta);
}

/**
 * フロー全体を実行するメイン関数
 * @param nodes - フロー内の全ノード
 * @param edges - ノード間の接続
 * @param log - ログ出力用コールバック
 * @param initialContext - 初期コンテキスト
 * @param onEdgeHighlight - エッジハイライト用コールバック
 * @param allowCycles - true の場合は一般ループを許可する（迷路アプリ用）
 */
export async function runFlow(
  nodes: AppNode[],
  edges: AppEdge[],
  log: Logger,
  initialContext: Partial<ExecContext> = {},
  onEdgeHighlight?: EdgeHighlighter,
  signal?: AbortSignal,
  reuseInitialContext = false,
  onNodeHighlight?: NodeHighlighter,
  allowCycles = false,
  displayLanguage: FlowDisplayLanguage = 'en',
) {
  const offlineIssue = OFFLINE_MODE ? findOfflineFlowIssue(nodes, edges, displayLanguage) : null;
  if (offlineIssue) {
    log(`エラー: ${offlineIssue}`);
    initialContext.onExecutionEvent?.({ type: 'run-error', message: offlineIssue });
    return;
  }
  const baseCtx: ExecContext = reuseInitialContext
    ? initialContext as ExecContext
    : { ...initialContext, signal };
  if (reuseInitialContext) {
    baseCtx.signal = signal;
  }
  let totalSteps = 0;
  let stoppedByBudget = false;
  let runtimeStopReason: string | null = null;
  const captureRuntimeStop = () => {
    runtimeStopReason ??= baseCtx.getRunStopReason?.() ?? null;
    return runtimeStopReason;
  };
  const isStopped = (): boolean => stoppedByBudget || signal?.aborted === true || captureRuntimeStop() !== null;
  const executeAndReport = async (
    node: AppNode,
    ctx: ExecContext,
    meta?: NodeRunMeta,
  ): Promise<NodeRunResult> => {
    ctx.onExecutionEvent?.({ type: 'node-started', nodeId: node.id, nodeKind: node.type });
    const result = await executeNode(node, ctx, log, meta);
    ctx.onExecutionEvent?.({ type: 'node-finished', nodeId: node.id, nodeKind: node.type, value: ctx.lastValue });
    return result;
  };
  const executeWithHighlight = async (
    node: AppNode,
    ctx: ExecContext,
    meta?: NodeRunMeta,
  ): Promise<NodeRunResult> => {
    if (!onNodeHighlight) return await executeAndReport(node, ctx, meta);
    const startedAt = Date.now();
    onNodeHighlight(node.id, true);
    try {
      return await executeAndReport(node, ctx, meta);
    } finally {
      const remaining = MIN_EDGE_HIGHLIGHT_MS - (Date.now() - startedAt);
      if (remaining > 0 && !isStopped()) {
        void wait(remaining, signal).then(() => onNodeHighlight(node.id, false));
      } else {
        onNodeHighlight(node.id, false);
      }
    }
  };

  if (signal?.aborted) {
    log('--- 中断しました ---');
    return;
  }

  // ノードIDからノードを高速に検索するためのマップ
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // 各ノードから出ているエッジのマップ（source → edges[]）
  const outMap = new Map<string, AppEdge[]>();
  for (const e of edges) {
    const list = outMap.get(e.source) ?? [];
    list.push(e);
    outMap.set(e.source, list);
  }

  // 通常フローは Start、関数フローは input から開始する
  const start = nodes.find((n): n is AppNodeOf<'start'> | AppNodeOf<'input'> =>
    n.type === 'start' || n.type === 'input'
  );
  if (!start) {
    log(nodes.some((node) => node.type === 'output')
      ? 'エラー: input が ありません'
      : 'エラー: Start が ありません');
    return;
  }

  // Startから出ているエッジを確認
  const startOut = outMap.get(start.id) ?? [];
  if (startOut.length === 0) {
    log(`エラー: ${start.type === 'start' ? 'Start' : 'input'} から せん が でていません`);
    return;
  }

  /**
   * 指定ノードから再帰的にフローを実行する内部関数
   * @param nodeId - 実行するノードのID
   * @param ctx - 実行コンテキスト（ノード間でデータを受け渡す）
   * @param path - 通過済みノードのセット（ループ検出用）
   * @param incomingEdge - このノードに入ってきたエッジ（ハイライト/ハンドル判定用）
   */
  const runFrom = async (nodeId: string, ctx: ExecContext, path: Set<string>, incomingEdge?: AppEdge): Promise<void> => {
    if (isStopped()) {
      return;
    }

    const incomingHighlightStartAt = incomingEdge && onEdgeHighlight ? Date.now() : null;
    let incomingHighlightCleared = false;
    const clearIncomingHighlight = async () => {
      if (!incomingEdge || !onEdgeHighlight || incomingHighlightCleared) {
        return;
      }
      incomingHighlightCleared = true;
      const elapsed = incomingHighlightStartAt == null ? 0 : Date.now() - incomingHighlightStartAt;
      const remaining = MIN_EDGE_HIGHLIGHT_MS - elapsed;
      if (remaining > 0 && !isStopped()) {
        await wait(remaining, signal);
      }
      onEdgeHighlight(incomingEdge.id, false);
    };

    // 入ってきたエッジをハイライト
    if (incomingEdge && onEdgeHighlight) {
      onEdgeHighlight(incomingEdge.id, true);
    }

    // ノードの存在確認
    const node = nodeMap.get(nodeId);
    if (!node || !node.type) {
      log(`エラー: ノードが みつかりません（${nodeId}）`);
      await clearIncomingHighlight();
      return;
    }

    // ノード種別の有効性チェック
    if (!isNodeKind(node.type)) {
      log(`エラー: 未対応のカードです（${node.type}）`);
      await clearIncomingHighlight();
      return;
    }

    const loopEntryHandle = nodeRegistry[node.type].loopEntryHandle;
    const isLoopEntry = loopEntryHandle != null && incomingEdge?.targetHandle === loopEntryHandle;

    // ループ検出：ノード定義で許可された入力に戻る経路のみ許可
    if (path.has(nodeId) && !isLoopEntry && !allowCycles) {
      log(`エラー: ループを みつけました（${nodeId}）`);
      await clearIncomingHighlight();
      return;
    }

    totalSteps += 1;
    if (totalSteps > MAX_TOTAL_STEPS) {
      if (!stoppedByBudget) {
        stoppedByBudget = true;
        log(`エラー: 実行ステップが多すぎます（上限 ${MAX_TOTAL_STEPS} 回）。フローを見直してください`);
      }
      await clearIncomingHighlight();
      return;
    }

    const nextPath = isLoopEntry ? new Set<string>([nodeId]) : new Set(path).add(nodeId);

    // ノードを実行
    let result: NodeRunResult;
    try {
      result = await executeWithHighlight(node, ctx, { incomingHandle: incomingEdge?.targetHandle ?? null });
    } catch (error) {
      if (signal?.aborted || error instanceof DOMException && error.name === 'AbortError') {
        await clearIncomingHighlight();
        return;
      }
      const message = error instanceof Error ? error.message : String(error);
      log(`エラー: カードの実行に しっぱいしました（${node.type}）: ${message}`);
      ctx.onExecutionEvent?.({ type: 'node-error', nodeId: node.id, nodeKind: node.type, message });
      ctx.onExecutionEvent?.({ type: 'run-error', message });
      await clearIncomingHighlight();
      return;
    }

    // ノード実行完了後、入ってきたエッジのハイライトを解除
    await clearIncomingHighlight();

    if (isStopped()) {
      return;
    }

    if (nodeRegistry[node.type].isTerminal) {
      ctx.onExecutionEvent?.({ type: 'terminal-reached', nodeId: node.id });
      return;
    }

    if (result && result.nextHandle === null) {
      return;
    }

    // 次のノードへ
    const outs = outMap.get(nodeId) ?? [];
    if (outs.length === 0) {
      log(`注意: この枝は End につながらずに終わりました（${nodeId}）`);
      return;
    }

    let branchEdges: AppEdge[] = outs;
    if (result && result.nextHandle != null) {
      branchEdges = pickBranchEdges(outs, result.nextHandle);
      if (branchEdges.length === 0) {
        log(`[${nodeRegistry[node.type].label}] 注意: ${result.branchLabel ?? result.nextHandle} につながる線がありません`);
        return;
      }
    }

    if (branchEdges.length === 1) {
      await runFrom(branchEdges[0].target, ctx, new Set(nextPath), branchEdges[0]);
      return;
    }

    await Promise.all(branchEdges.map((e) => runFrom(e.target, cloneCtx(ctx), new Set(nextPath), e)));
  };

  const synthesize = baseCtx.robotService?.synthesizeSpeech ?? baseCtx.synthesizeSpeech;
  const ownedPrefetch = !baseCtx.speechPrefetch && synthesize
    ? new SpeechPrefetch(synthesize, log, signal)
    : undefined;
  if (ownedPrefetch) baseCtx.speechPrefetch = ownedPrefetch;
  try {
    if (ownedPrefetch) {
      const requests = collectSpeechPlan(nodes, edges, baseCtx.lastValue);
      if (requests.length) log(`[TTS先読み] ${requests.length}件を順次取得します。最初の1件を準備中…`);
      let firstAudioReady = true;
      try {
        await ownedPrefetch.start(requests);
      } catch (error) {
        if (!OFFLINE_MODE || signal?.aborted || error instanceof DOMException && error.name === 'AbortError') throw error;
        firstAudioReady = false;
      }
      if (requests.length && firstAudioReady) log('[TTS先読み] 最初の音声の取得完了。残りは実行中に取得します');
    }
    if (isStopped()) return;
    log('--- 実行開始 ---');
    let shouldContinue = true;
    try {
      await executeWithHighlight(start, baseCtx);
    } catch (error) {
      shouldContinue = false;
      if (!(signal?.aborted || error instanceof DOMException && error.name === 'AbortError')) {
        log(`エラー: カードの実行に しっぱいしました（${start.type}）: ${String(error)}`);
        baseCtx.onExecutionEvent?.({ type: 'run-error', message: String(error) });
      }
    }

    // Startから出ている全ての枝を並列に実行
    if (shouldContinue && !isStopped()) {
      await Promise.all(startOut.map((e) => runFrom(
        e.target,
        reuseInitialContext && startOut.length === 1 ? baseCtx : cloneCtx(baseCtx),
        new Set([start.id]),
        e,
      )));
    }

    if (signal?.aborted) {
      log('--- 中断しました ---');
    } else if (stoppedByBudget) {
      log('--- 実行を打ち切りました ---');
    } else if (runtimeStopReason) {
      log(`--- 実行停止: ${runtimeStopReason} ---`);
    } else {
      log('--- 実行終了 ---');
    }
  } catch (error) {
    if (signal?.aborted || error instanceof DOMException && error.name === 'AbortError') {
      log('--- 中断しました ---');
    } else {
      const message = error instanceof Error ? error.message : String(error);
      log(`エラー: フローの準備・実行に失敗しました: ${message}`);
      baseCtx.onExecutionEvent?.({ type: 'run-error', message });
    }
  } finally {
    ownedPrefetch?.dispose();
    if (ownedPrefetch) delete baseCtx.speechPrefetch;
  }
}
